import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LocalizedNavLink from '../hooks/LocalizedNavLink';
import { useTranslation } from 'react-i18next';
import FooterSkeleton from './FooterSkeleton';
import FooterLogo from './../assets/images/logo__black.svg';
import { useFontAwesomeIcon } from '../hooks/useFontAwesomeIcon';
import { useHeaderFooterCache } from '../hooks/useHeaderFooterCache';
import { AnimatedOnScroll } from './AnimatedOnScroll';
import './Footer.scss';

const Footer = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    const location = useLocation();
    const navigate = useNavigate();
    const { getIcon } = useFontAwesomeIcon();

    const { getFooterData, isLoading, hasError } = useHeaderFooterCache();

    const footerData = getFooterData(currentLang);

    const isContactPage = useMemo(() => {
        return location.pathname === '/contact' || location.pathname === `/${currentLang}/contact`;
    }, [location.pathname, currentLang]);

    const handleLogoClick = (e) => {
        e.preventDefault();
        const targetPath = `/${currentLang}`;

        if (location.pathname === targetPath || location.pathname === targetPath + '/') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            navigate(targetPath);
        }
    };

    const getUniqueCategories = useMemo(() => {
        return (articles) => {
            if (!articles || !Array.isArray(articles)) return [];
            const categories = articles.map((article) => article.webLink?.category).filter(Boolean);
            const uniqueCategories = [...new Set(categories.map((category) => category.slug))].map((slug) =>
                categories.find((category) => category.slug === slug)
            );
            return uniqueCategories;
        };
    }, []);

    if (isLoading) {
        return <FooterSkeleton />;
    }

    if (hasError || !footerData?.isValid) {
        return null;
    }

    const { mainContent, generalContent, articleData } = footerData;
    const validServices = mainContent.Services.filter(service => service.title && service.path);
    const validCompanyLinks = generalContent.Company.filter(item => item.title && item.path);
    const uniqueCategories = getUniqueCategories(articleData);

    return (
        <footer className='footer'>
            <div className='sitemap__wrapper'>
                <div className='sitemap__company__item'>
                    <div className='footer__company__logo'>
                        <LocalizedNavLink className='link__logo' to='/' onClick={handleLogoClick}>
                            <img className='logo' src={FooterLogo} alt='Logo-Design-Studio' loading="lazy" />
                        </LocalizedNavLink>
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <p className='footer__company__text'>
                                {mainContent.FooterArticle}
                            </p>
                        </AnimatedOnScroll>
                    </div>
                    <div className='footer__links__wrapper'>
                        {Object.values(generalContent.SocialLinks).map((social) => (
                            <div key={social.id} className="socials__item">
                                <a
                                    className="socials__link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={social.shareUrl}
                                    aria-label={social.name}
                                >
                                    {getIcon(social.iconType, social.iconClass)}
                                </a>

                            </div>
                        ))}
                    </div>
                </div>

                <div className='sitemap__list__wrapper'>
                    <div className='sitemap__list__item'>
                        <h6 className='sitemap__list__item__subtitle'>
                            {mainContent.MarketingTitle}
                        </h6>
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <ul className='nav__list'>
                                {mainContent.MarketingServices.map((service, index) => (
                                    <li key={index} className="list__item">
                                        <LocalizedNavLink className="nav__item__link" to={`/marketing-and-advertising${service.path}`}>
                                            {service.title}
                                        </LocalizedNavLink>
                                    </li>
                                ))}
                            </ul>
                        </AnimatedOnScroll>
                    </div>

                    <div className='sitemap__list__item'>
                        <h6 className='sitemap__list__item__subtitle'>
                            {mainContent.ServicesTitle}
                        </h6>
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <ul className='nav__list'>
                                {validServices.map((service, index) => (
                                    <li key={index} className="list__item">
                                        <LocalizedNavLink className="nav__item__link" to={service.path}>
                                            {service.title}
                                        </LocalizedNavLink>
                                    </li>
                                ))}
                            </ul>
                        </AnimatedOnScroll>
                    </div>

                    <div className='sitemap__list__item'>
                        <h6 className='sitemap__list__item__subtitle'>
                            {mainContent.InsightsTitle}
                        </h6>
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <ul className='nav__list'>
                                {uniqueCategories.map((category) => (
                                    <li className='list__item' key={category.slug}>
                                        <LocalizedNavLink className='nav__item__link' to={`/blog/${category.slug}`}>
                                            {typeof category.categoryName === 'object'
                                                ? category.categoryName[currentLang] || category.categoryName.en
                                                : category.categoryName
                                            }
                                        </LocalizedNavLink>
                                    </li>
                                ))}
                            </ul>
                        </AnimatedOnScroll>
                    </div>

                    <div className='sitemap__list__item sitemap__list__item--primary'>
                        <h6 className='sitemap__list__item__subtitle'>
                            {mainContent.CompanyTitle}
                        </h6>
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <ul className='nav__list'>
                                {validCompanyLinks.map((link, index) => (
                                    <li key={index} className="list__item">
                                        <LocalizedNavLink
                                            className={({ isActive }) => (isActive ? "nav__item__link active" : "nav__item__link")}
                                            to={link.path}
                                        >
                                            {link.title}
                                        </LocalizedNavLink>
                                    </li>
                                ))}
                            </ul>
                        </AnimatedOnScroll>
                    </div>
                </div>

                <div className='sitemap__contact__wrapper'>
                    {!isContactPage && (
                        <LocalizedNavLink to={mainContent.FooterButton.link} className='recent__work__button'>
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                            {mainContent.FooterButton.text}
                        </LocalizedNavLink>
                    )}
                    <h6 className='sitemap__contact__item__subtitle'>
                        {mainContent.FooterSubtitle}
                    </h6>
                    <AnimatedOnScroll animation="fade-up" delay={0}>
                        <a className='email__link' href={generalContent.EmailLink.link}>
                            {generalContent.EmailLink.text}
                        </a>
                    </AnimatedOnScroll>
                </div>
            </div>

            <div className='reserved'>
                <p className='reserved__text'>{mainContent.Copyright}</p>
                <div className='reserved__links__item__wrapper'>
                    {currentLang === 'de' && mainContent.Impressum && (
                        <LocalizedNavLink
                            className={({ isActive }) => `reserved__links__item ${isActive ? 'active' : ''}`}
                            to={mainContent.Impressum.link}
                        >
                            {mainContent.Impressum.text}
                        </LocalizedNavLink>
                    )}
                    <LocalizedNavLink
                        className={({ isActive }) => `reserved__links__item ${isActive ? 'active' : ''}`}
                        to={mainContent.TermsLink.link}
                    >
                        {mainContent.TermsLink.text}
                    </LocalizedNavLink>
                    <LocalizedNavLink
                        className={({ isActive }) => `reserved__links__item ${isActive ? 'active' : ''}`}
                        to={mainContent.PrivacyLink.link}
                    >
                        {mainContent.PrivacyLink.text}
                    </LocalizedNavLink>
                    <LocalizedNavLink
                        className={({ isActive }) => `reserved__links__item ${isActive ? 'active' : ''}`}
                        to={mainContent.SitemapLink.link}
                    >
                        {mainContent.SitemapLink.text}
                    </LocalizedNavLink>
                </div>
            </div>
        </footer>
    );
};

export default React.memo(Footer);